import { useEffect, useMemo, useRef, useState } from 'react';

import { EventDeviceGpsPage, getEventDeviceGpsPage } from '../services/fleet';
import { simplifyTrackRDP } from '../plugins/synced_viewer/util';
import { GpsSummary } from '../types';

const POLL_MS = 30_000;
const MAX_TRACK_POINTS = 4096;
const MAX_CACHED_POINTS = 100_000;
// A `more` page is already-waiting data, not a retry — but a cap keeps a
// misbehaving backend (`more` never clearing) from turning one poll tick
// into an unbounded fetch loop.
const MAX_CATCH_UP_PAGES = 5;

type GpsPoint = GpsSummary['track'][number];

interface DeviceGpsCache {
  points: Map<number, GpsPoint>;
  afterId: number | null;
}

interface CacheEntry {
  key: string;
  track: GpsSummary['track'];
}

export interface EventDeviceGpsResult {
  track: GpsSummary['track'];
  loading: boolean;
}

function simplify(track: GpsSummary['track']): GpsSummary['track'] {
  return track.length <= MAX_TRACK_POINTS
    ? track
    : simplifyTrackRDP(track, 0.1, MAX_TRACK_POINTS);
}

/** Merge the polled event path with the fresher session/SSE trail. */
export function mergeGpsTracks(
  eventTrack: GpsSummary['track'],
  sessionTrack: GpsSummary['track'],
): GpsSummary['track'] {
  if (eventTrack.length === 0) return sessionTrack;
  if (sessionTrack.length === 0) return eventTrack;
  const merged = eventTrack.concat(sessionTrack).sort((a, b) => a[2] - b[2]);
  const unique: GpsSummary['track'] = [];
  let lastTs = -Infinity;
  for (const point of merged) {
    if (point[2] <= lastTs) continue;
    unique.push(point);
    lastTs = point[2];
  }
  return simplify(unique);
}

/**
 * Apply one cursor page to the id-keyed cache: a Map write de-dupes a
 * repeated id. The first call's `next_after_id` is the newest-*ts* row's
 * id, not necessarily the highest id seen (ties/clock skew can differ), so
 * a later page can legitimately carry an id lower than one already
 * cached — eviction picks the lowest id explicitly rather than assuming
 * Map insertion order tracks it.
 */
export function mergeGpsPage(
  points: Map<number, GpsPoint>,
  page: EventDeviceGpsPage['track'],
  maxCachedPoints: number = MAX_CACHED_POINTS,
): void {
  for (const [lat, lon, utcMs, speedMps, id] of page) {
    points.set(id, [lat, lon, utcMs, speedMps]);
  }
  // One sort of the full key set, not a full re-scan per evicted id: at the
  // 100k bound a multi-page catch-up (see MAX_CATCH_UP_PAGES) evicting one
  // key at a time is 1-2 orders of magnitude slower and blocks the main
  // thread for seconds.
  const excess = points.size - maxCachedPoints;
  if (excess > 0) {
    const ids = Array.from(points.keys()).sort((a, b) => a - b);
    for (let i = 0; i < excess; i += 1) points.delete(ids[i]);
  }
}

/**
 * Sort the id-keyed cache into a display track. A corrected fix can repeat
 * an earlier one's utc_ms (see the backend's EventDeviceGpsCursorOut
 * docstring), so equal timestamps collapse to the higher-id sample —
 * otherwise the polyline would double back on itself at that point.
 */
export function toDisplayTrack(points: Map<number, GpsPoint>): GpsSummary['track'] {
  const sorted = Array.from(points.entries()).sort(
    ([idA, a], [idB, b]) => a[2] - b[2] || idA - idB,
  );
  const out: GpsSummary['track'] = [];
  for (const [, point] of sorted) {
    if (out.length > 0 && out[out.length - 1][2] === point[2]) {
      out[out.length - 1] = point;
    } else {
      out.push(point);
    }
  }
  return out;
}

/** Event-wide competitor path, including GPS received between recordings. */
export function useEventDeviceGps(
  eventId: number,
  deviceId: string | null,
  enabled: boolean,
): EventDeviceGpsResult {
  const key = enabled && deviceId ? `${eventId}:${deviceId}` : '';
  const cacheRef = useRef<Map<string, DeviceGpsCache>>(new Map());
  const [entry, setEntry] = useState<CacheEntry | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!key || !deviceId) {
      setEntry(null);
      setLoading(false);
      return;
    }
    let cache = cacheRef.current.get(key);
    setEntry(cache ? { key, track: simplify(toDisplayTrack(cache.points)) } : null);
    let cancelled = false;
    // Local to this effect run (one per key): guards against a slow
    // request still in flight when the next 30 s tick fires, which would
    // otherwise start a second fetch from the same stale `cache.afterId`.
    let fetching = false;

    const fetchResolvingReload = async (
      afterId: number | undefined,
    ): Promise<EventDeviceGpsPage> => {
      const page = await getEventDeviceGpsPage(eventId, deviceId, afterId);
      if (!page.reload) return page;
      cache = { points: new Map(), afterId: null };
      return getEventDeviceGpsPage(eventId, deviceId, undefined);
    };

    const refresh = async () => {
      if (fetching) return;
      fetching = true;
      if (!cache) setLoading(true);
      try {
        let page = await fetchResolvingReload(cache?.afterId ?? undefined);
        if (!cache) cache = { points: new Map(), afterId: null };

        for (let catchUp = 0; ; catchUp += 1) {
          mergeGpsPage(cache.points, page.track);
          cache.afterId = page.next_after_id;
          cacheRef.current.set(key, cache);
          if (cancelled) return;
          if (!page.more || cache.afterId === null || catchUp >= MAX_CATCH_UP_PAGES) {
            break;
          }
          page = await fetchResolvingReload(cache.afterId);
        }

        setEntry({ key, track: simplify(toDisplayTrack(cache.points)) });
      } catch (error) {
        // Rolling deploys may briefly have the new viewer before the backend
        // route. The caller retains its session-only fallback in that case.
        console.warn('[useEventDeviceGps] event path fetch failed', error);
      } finally {
        fetching = false;
        if (!cancelled) setLoading(false);
      }
    };

    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [deviceId, eventId, key]);

  return useMemo(
    () => ({
      track: entry?.key === key ? entry.track : [],
      loading,
    }),
    [entry, key, loading],
  );
}

export default useEventDeviceGps;
